import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError, imageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Buildings, Plus, X, UploadSimple, UserPlus, ChartLineUp, ArrowSquareOut,
  ShieldCheck, MapPin, Envelope, Phone, Globe, User, Trash, WarningCircle, Copy,
} from "@phosphor-icons/react";

export function AgencyPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [addingEmail, setAddingEmail] = useState("");
  const [addingLoading, setAddingLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", logo_url: null, phone: "", email: "",
    website: "", address: "", city: "", state: "", coverage_zones: [],
  });
  const [zoneInput, setZoneInput] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agencies/me");
      setAgency(data);
      if (data) {
        setForm({
          name: data.name || "", description: data.description || "",
          logo_url: data.logo_url || null, phone: data.phone || "",
          email: data.email || "", website: data.website || "",
          address: data.address || "", city: data.city || "", state: data.state || "",
          coverage_zones: data.coverage_zones || [],
        });
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createAgency = async (e) => {
    e.preventDefault();
    if (!newName.trim() || newName.trim().length < 2) return toast.error("Ingresa un nombre válido");
    setCreating(true);
    try {
      await api.post("/agencies", { name: newName.trim() });
      toast.success("Agencia creada");
      await refresh();
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, logo_url: data.url }));
      toast.success("Logo cargado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingLogo(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/agencies/me", form);
      setAgency(a => ({ ...a, ...data }));
      setEditing(false);
      toast.success("Perfil de agencia actualizado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingProfile(false); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    if (!addingEmail.trim()) return;
    setAddingLoading(true);
    try {
      await api.post("/agencies/me/members", { email: addingEmail.trim().toLowerCase() });
      toast.success("Asesor añadido al equipo");
      setAddingEmail("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAddingLoading(false); }
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`¿Quitar a ${name} del equipo?`)) return;
    try {
      await api.delete(`/agencies/me/members/${uid}`);
      toast.success("Asesor removido");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const leaveAgency = async () => {
    if (!window.confirm("¿Salir de esta agencia?")) return;
    try {
      await api.post("/agencies/me/leave");
      toast.success("Saliste de la agencia");
      await refresh();
      setAgency(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteAgency = async () => {
    if (!window.confirm("¿Eliminar la agencia? Los asesores quedarán como individuales. Esta acción no se puede deshacer.")) return;
    try {
      await api.delete("/agencies/me");
      toast.success("Agencia eliminada");
      await refresh();
      setAgency(null);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return (
    <div className="max-w-5xl mx-auto px-6 py-16 animate-pulse space-y-4">
      <div className="h-10 w-64 bg-[#1A1A1A] rounded" />
      <div className="h-40 bg-[#1A1A1A] rounded-2xl" />
    </div>
  );

  // No agency yet — offer creation.
  if (!agency) return (
    <div className="max-w-3xl mx-auto px-6 py-12 lg:py-16" data-testid="agency-page-empty">
      <Link to="/dashboard" className="text-xs text-[#C8A24A]/70 hover:text-[#C8A24A] inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Panel
      </Link>
      <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold mt-4">Agencia</p>
      <h1 className="font-heading text-4xl lg:text-5xl mt-2 tracking-tight">Construye tu agencia</h1>
      <p className="text-sm text-[#C8A24A]/70 mt-3 max-w-xl">
        Crea una agencia para administrar un equipo de asesores, ver rendimiento consolidado y presentar
        una marca profesional a los compradores. Puedes seguir operando en solitario si prefieres.
      </p>
      <form onSubmit={createAgency} className="mt-8 bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6" data-testid="agency-create-form">
        <Label htmlFor="agency-name" className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">Nombre de la agencia</Label>
        <Input
          id="agency-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Ej. Rocafuerte Norte"
          className="mt-2"
          data-testid="agency-name-input"
        />
        <Button
          type="submit"
          disabled={creating}
          className="mt-4 bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6"
          data-testid="agency-create-btn"
        >
          <Plus size={16} className="mr-1" /> {creating ? "Creando..." : "Crear agencia"}
        </Button>
      </form>
    </div>
  );

  // Agency exists.
  const isOwner = agency.is_owner;
  const publicUrl = `${window.location.origin}/agencia/${agency.slug}`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="agency-page">
      <Link to="/dashboard" className="text-xs text-[#C8A24A]/70 hover:text-[#C8A24A] inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Panel
      </Link>
      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#1A1A1A] border border-[#C8A24A]/30 flex items-center justify-center overflow-hidden">
            {agency.logo_url ? (
              <img src={imageUrl(agency.logo_url)} alt={agency.name} className="w-full h-full object-cover" />
            ) : (
              <Buildings size={28} weight="duotone" className="text-[#C8A24A]" />
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Agencia · {isOwner ? "Propietario" : "Miembro"}</p>
            <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight" data-testid="agency-title">{agency.name}</h1>
            <a
              href={`/agencia/${agency.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#C8A24A]/60 hover:text-[#C8A24A] font-mono inline-flex items-center gap-1 mt-1"
              data-testid="agency-public-url"
            >
              /agencia/{agency.slug} <ArrowSquareOut size={11} />
            </a>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/dashboard/agencia/analytics">
            <Button className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11" data-testid="agency-analytics-btn">
              <ChartLineUp size={16} className="mr-1" /> Analíticas del equipo
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); }}
            className="h-11"
            data-testid="agency-copy-url-btn"
          >
            <Copy size={14} className="mr-1" /> Copiar URL
          </Button>
        </div>
      </div>

      {/* Members grid */}
      <section className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-6 mb-6" data-testid="agency-members-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Equipo</p>
            <h2 className="font-heading text-2xl mt-1">{agency.members.length} asesor(es)</h2>
          </div>
        </div>
        {isOwner && (
          <form onSubmit={addMember} className="flex gap-2 mb-4" data-testid="agency-add-member-form">
            <Input
              value={addingEmail}
              onChange={(e) => setAddingEmail(e.target.value)}
              placeholder="Correo del asesor a invitar"
              type="email"
              data-testid="agency-add-member-input"
            />
            <Button
              type="submit"
              disabled={addingLoading || !addingEmail.trim()}
              className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]"
              data-testid="agency-add-member-btn"
            >
              <UserPlus size={14} className="mr-1" /> {addingLoading ? "Añadiendo..." : "Añadir"}
            </Button>
          </form>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agency-members-grid">
          {agency.members.map(m => {
            const initials = (m.name || "??").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
            const isOwnerMember = m.agency_role === "owner";
            return (
              <div key={m.id} className="p-4 rounded-xl bg-[#0A0A0A] border border-[#C8A24A]/10 flex items-start gap-3" data-testid={`agency-member-${m.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] border border-[#C8A24A]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {m.photo_url ? (
                    <img src={imageUrl(m.photo_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-heading font-bold text-[#C8A24A]">{initials}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{m.name}</p>
                  <p className="text-xs text-[#C8A24A]/60 truncate">{m.email}</p>
                  <p className="text-xs uppercase tracking-widest mt-1 font-bold inline-flex items-center gap-1">
                    {isOwnerMember ? <><ShieldCheck size={10} className="text-[#C8A24A]" /> <span className="text-[#C8A24A]">Propietario</span></> : <span className="text-[#C8A24A]/60">Asesor</span>}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {m.slug && (
                    <a href={`/asesor/${m.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#C8A24A]/60 hover:text-[#C8A24A]" title="Ver perfil" data-testid={`agency-member-view-${m.id}`}>
                      <ArrowSquareOut size={14} />
                    </a>
                  )}
                  {isOwner && !isOwnerMember && (
                    <button
                      type="button"
                      onClick={() => removeMember(m.id, m.name)}
                      className="text-red-400/70 hover:text-red-300"
                      title="Quitar del equipo"
                      data-testid={`agency-member-remove-${m.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Profile editor */}
      {isOwner && (
        <section className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-6 mb-6" data-testid="agency-profile-card">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">Perfil de agencia</p>
              <h2 className="font-heading text-2xl mt-1">Marca profesional</h2>
              <p className="text-xs text-[#C8A24A]/60 mt-1">Así te ven los compradores en tu página pública.</p>
            </div>
            <Button variant="outline" onClick={() => setEditing(v => !v)} data-testid="agency-edit-toggle">
              {editing ? "Cancelar" : "Editar"}
            </Button>
          </div>
          {editing ? (
            <form onSubmit={saveProfile} className="grid gap-4" data-testid="agency-edit-form">
              <div>
                <Label className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">Logo</Label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-[#0A0A0A] border border-[#C8A24A]/20 flex items-center justify-center overflow-hidden">
                    {form.logo_url ? (
                      <img src={imageUrl(form.logo_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Buildings size={24} className="text-[#C8A24A]/30" />
                    )}
                  </div>
                  <label className="cursor-pointer inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10 text-xs font-semibold">
                    <UploadSimple size={14} /> {uploadingLogo ? "Subiendo..." : (form.logo_url ? "Cambiar" : "Subir")}
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo}
                      onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} data-testid="agency-logo-input" />
                  </label>
                </div>
              </div>
              <FormField label="Nombre" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testId="agency-form-name" />
              <div>
                <Label className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80">Descripción</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value.slice(0, 2000) }))} className="mt-2" data-testid="agency-form-description" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField icon={Envelope} label="Correo público" value={form.email} onChange={(v) => setForm(f => ({ ...f, email: v }))} testId="agency-form-email" type="email" />
                <FormField icon={Phone} label="Teléfono público" value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} testId="agency-form-phone" />
              </div>
              <FormField icon={Globe} label="Sitio web" value={form.website} onChange={(v) => setForm(f => ({ ...f, website: v }))} testId="agency-form-website" placeholder="https://..." />
              <div className="grid sm:grid-cols-3 gap-4">
                <FormField label="Dirección" value={form.address} onChange={(v) => setForm(f => ({ ...f, address: v }))} testId="agency-form-address" />
                <FormField label="Ciudad" value={form.city} onChange={(v) => setForm(f => ({ ...f, city: v }))} testId="agency-form-city" />
                <FormField label="Estado" value={form.state} onChange={(v) => setForm(f => ({ ...f, state: v }))} testId="agency-form-state" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80 inline-flex items-center gap-1.5">
                  <MapPin size={12} /> Zonas de cobertura
                </Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={zoneInput}
                    onChange={(e) => setZoneInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = zoneInput.trim();
                        if (t && !form.coverage_zones.includes(t)) {
                          setForm(f => ({ ...f, coverage_zones: [...f.coverage_zones, t] }));
                        }
                        setZoneInput("");
                      }
                    }}
                    placeholder="Ej. Polanco, San Pedro"
                    data-testid="agency-form-zone-input"
                  />
                </div>
                {form.coverage_zones.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="agency-form-zones">
                    {form.coverage_zones.map((z, i) => (
                      <span key={z + i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#0A0A0A] border border-[#C8A24A]/30 text-[#C8A24A]/90">
                        {z}
                        <button type="button" onClick={() => setForm(f => ({ ...f, coverage_zones: f.coverage_zones.filter((_, idx) => idx !== i) }))} data-testid={`agency-form-zone-remove-${i}`}>
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Button type="submit" disabled={savingProfile} className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6" data-testid="agency-form-save-btn">
                  {savingProfile ? "Guardando..." : "Guardar perfil"}
                </Button>
              </div>
            </form>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <ReadOnlyField label="Descripción" value={agency.description || "—"} />
              <ReadOnlyField label="Correo público" value={agency.email || "—"} />
              <ReadOnlyField label="Teléfono público" value={agency.phone || "—"} />
              <ReadOnlyField label="Sitio web" value={agency.website || "—"} />
              <ReadOnlyField label="Dirección" value={[agency.address, agency.city, agency.state].filter(Boolean).join(", ") || "—"} />
              <ReadOnlyField label="Zonas de cobertura" value={agency.coverage_zones?.join(" · ") || "—"} />
            </div>
          )}
        </section>
      )}

      {/* Danger zone */}
      <section className="bg-[#1A1A1A] rounded-2xl border border-red-500/20 p-5 sm:p-6" data-testid="agency-danger-card">
        <p className="text-xs uppercase tracking-widest text-red-300/70 font-semibold inline-flex items-center gap-1.5">
          <WarningCircle size={11} weight="fill" /> Zona sensible
        </p>
        {isOwner ? (
          <>
            <h3 className="font-heading text-lg mt-1">Eliminar agencia</h3>
            <p className="text-xs text-[#C8A24A]/60 mt-1">Los asesores quedan como individuales. Sus propiedades y leads se conservan.</p>
            <Button variant="outline" onClick={deleteAgency} className="mt-3 h-10 border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="agency-delete-btn">
              <Trash size={14} className="mr-1" /> Eliminar agencia
            </Button>
          </>
        ) : (
          <>
            <h3 className="font-heading text-lg mt-1">Salir de la agencia</h3>
            <p className="text-xs text-[#C8A24A]/60 mt-1">Volverás a operar como asesor individual. Puedes ser reinvitado luego.</p>
            <Button variant="outline" onClick={leaveAgency} className="mt-3 h-10 border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="agency-leave-btn">
              Salir del equipo
            </Button>
          </>
        )}
      </section>
    </div>
  );
}

function FormField({ label, value, onChange, testId, type = "text", placeholder, icon: Icon }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-semibold text-[#C8A24A]/80 inline-flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {label}
      </Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-2" data-testid={testId} />
    </div>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold">{label}</p>
      <p className="text-sm text-[#C8A24A]/90 mt-1 break-words">{value}</p>
    </div>
  );
}
